import { useEffect, useMemo } from 'react';
import { debounce } from 'es-toolkit';

import { logger } from '@zep/core-logger';

import { QuizsetCursorSearchReqContentType } from '@/api/gen/_model/quizsetCursorSearchReqContentType';
import {
  type GetAllQuizesPayload,
  usePublicQuizListPayload,
} from '@/module/public/hooks/useGetAllQuizesPayload';
import { useLibraryContentTypeQuery } from '@/module/public/hooks/useLibraryContentTypeQuery';
import { useSearchLibrary } from '@/shared/features/quiz-library';
import { useCursorQueryRecovery } from '@/shared/hooks/useCursorQueryRecovery';

const logEvent = ({
  categoryFilters,
  subjectFilter,
  totalQuizCount,
  order,
  period,
  libraryContentType,
}: Omit<GetAllQuizesPayload, 'enabled'> & {
  totalQuizCount?: number;
  libraryContentType: string;
}) => {
  logger.track('filter_search', {
    ...categoryFilters,
    ...subjectFilter,
    templateType: categoryFilters.templateType ?? 'all',
    ...(totalQuizCount != null && { totalQuizCount }),
    order,
    period,
    // `content_type` 은 이미 quiz/board 의미로 쓰여 자료실 전용 이름을 쓴다.
    library_content_type: libraryContentType,
  });
};

const sharedDebouncedLogger = debounce(logEvent, 1000);

/** 자료실 목록. 퀴즈와 퀴즈 폴더가 하나의 커서로 섞여 내려온다 (`POST /quiz-library/search`). */
export const usePublicLibraryList = () => {
  const { enabled, categoryFilters, subjectFilter, order, period } =
    usePublicQuizListPayload();
  const { contentType } = useLibraryContentTypeQuery();

  const result = useSearchLibrary({
    enabled,
    variables: {
      contentType,
      categoryFilters: {
        criteria: categoryFilters,
        ...subjectFilter,
      },
      order,
      period,
    },
  });

  // 서버가 커서 형식을 바꿔 배포하면 그 전에 스크롤하던 탭은 다음 페이지에서 반드시 거절당한다.
  // 오류 화면 대신 첫 페이지부터 한 번 다시 읽는다.
  const { isRecovering, shouldMaskError } = useCursorQueryRecovery({
    isError: result.isError,
    error: result.error,
    hasLoadedPages:
      !result.isPlaceholderData && (result.data?.pages.length ?? 0) > 0,
    refetch: result.refetch,
  });

  const flattenedData = useMemo(
    () => result.data?.pages.flatMap(page => page.items) ?? [],
    [result.data],
  );

  useEffect(() => {
    if (!result.isPlaceholderData && result.data?.pages.length === 1) {
      const firstPage = result.data.pages[0];
      sharedDebouncedLogger({
        categoryFilters,
        subjectFilter,
        order,
        period,
        libraryContentType: contentType,
        // 혼합·폴더 검색의 totalCount 는 폴더까지 세므로 퀴즈 수로 기록하지 않는다.
        totalQuizCount:
          contentType === QuizsetCursorSearchReqContentType.QUIZSET
            ? firstPage.totalCount ?? undefined
            : undefined,
      });
    }
  }, [
    categoryFilters,
    subjectFilter,
    order,
    period,
    contentType,
    result.data?.pages,
    result.isPlaceholderData,
  ]);

  return {
    ...result,
    isError: result.isError && !shouldMaskError,
    isFetchingNextPage: result.isFetchingNextPage || isRecovering,
    // 필터가 준비되기 전에는 아직 아무것도 못 받은 상태다.
    isLoading: !enabled || result.isLoading,
    flatData: flattenedData,
  };
};
